// src/components/ShapesToolbar.js
import { useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardHeader,
  Stack,
} from "@mui/material";
import {
  CropSquareSharp as CropSquareSharpIcon,
  CircleOutlined as CircleOutlinedIcon,
  ChangeHistory as ChangeHistoryIcon,
} from "@mui/icons-material";
import { Rect, Circle, Triangle, Ellipse } from "fabric";
import { useData } from "../hooks/useData";
import PolygonToolbar from "./PolygonToolbar";
import LineToolbar from "./LineToolbar";

const ShapesToolbar = ({ canvas }) => {
  const { setSelectedTool, setSelectedObject, canvasScale, setSnackbar } =
    useData();

  useEffect(() => {
    if (canvas) {
      canvas.on("selection:created", () => {
        // Keep ShapesToolbar active
      });
      canvas.on("selection:cleared", () => {
        // Optional: setSelectedTool(null) to revert to default
      });
    }
  }, [canvas, setSelectedTool]);

  const addRectangle = () => {
    if (!canvas || !canvasScale) {
      setSnackbar({
        open: true,
        message: "Canvas or scale not initialized.",
        severity: "error",
      });
      return;
    }

    console.log("ShapesToolbar: Checking fabric.Rect", {
      Rect: Rect,
      fabricVersion: Rect.version || "undefined",
      canvasMethods: Object.keys(canvas),
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      viewportTransform: canvas.viewportTransform,
      zoom: canvas.getZoom(),
    });
    if (!Rect) {
      console.error("ShapesToolbar: Rect is not defined");
      setSnackbar({
        open: true,
        message: "Failed to add rectangle. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    try {
      // Reset viewport and zoom
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvas.setZoom(1);
      const rect = new Rect({
        top: canvas.getHeight() / 2,
        left: canvas.getWidth() / 2,
        width: 100,
        height: 60,
        fill: "#D84D42",
        strokeUniform: true,
        originX: "center",
        originY: "center",
        opacity: 1,
      });
      canvas.add(rect);
      canvas.setActiveObject(rect);
      setSelectedObject(rect);
      // Ensure rectangle is on top
      try {
        if (typeof canvas.bringObjectToFront === "function") {
          canvas.bringObjectToFront(rect);
          console.log("ShapesToolbar: Used bringObjectToFront for rectangle");
        } else {
          console.warn("ShapesToolbar: bringObjectToFront not available");
          const objects = canvas.getObjects();
          const index = objects.indexOf(rect);
          if (index !== -1 && index < objects.length - 1) {
            objects.splice(index, 1);
            objects.push(rect);
            canvas._objects = objects;
            console.log(
              "ShapesToolbar: Manually reordered stack for rectangle"
            );
          }
        }
      } catch (stackError) {
        console.error(
          "ShapesToolbar: Failed to adjust rectangle stacking",
          stackError
        );
      }
      canvas.requestRenderAll();
      const bounds = rect.getBoundingRect();
      console.log("ShapesToolbar: Rectangle added", {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
        canvasWidth: canvas.getWidth(),
        canvasHeight: canvas.getHeight(),
        stackIndex: canvas.getObjects().indexOf(rect),
        objectCount: canvas.getObjects().length,
        objects: canvas.getObjects().map((obj) => ({
          type: obj.type,
          top: obj.top,
          left: obj.left,
        })),
        bounds: {
          left: bounds.left,
          top: bounds.top,
          width: bounds.width,
          height: bounds.height,
        },
        isVisible:
          bounds.left >= 0 &&
          bounds.top >= 0 &&
          bounds.left + bounds.width <= canvas.getWidth() &&
          bounds.top + bounds.height <= canvas.getHeight(),
      });
    } catch (error) {
      console.error("ShapesToolbar: Failed to add rectangle", error);
      setSnackbar({
        open: true,
        message: "Error adding rectangle: " + error.message,
        severity: "error",
      });
    }
  };

  const addCircle = () => {
    if (!canvas || !canvasScale) {
      setSnackbar({
        open: true,
        message: "Canvas or scale not initialized.",
        severity: "error",
      });
      return;
    }

    console.log("ShapesToolbar: Checking fabric.Circle", {
      Circle: Circle,
      fabricVersion: Circle.version || "undefined",
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      viewportTransform: canvas.viewportTransform,
      zoom: canvas.getZoom(),
    });
    if (!Circle) {
      console.error("ShapesToolbar: Circle is not defined");
      setSnackbar({
        open: true,
        message: "Failed to add circle. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    try {
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvas.setZoom(1);
      const circle = new Circle({
        top: canvas.getHeight() / 2,
        left: canvas.getWidth() / 2,
        radius: 50,
        fill: "#2F4DC6",
        strokeUniform: true,
        originX: "center",
        originY: "center",
        opacity: 1,
      });
      canvas.add(circle);
      canvas.setActiveObject(circle);
      setSelectedObject(circle);
      try {
        if (typeof canvas.bringObjectToFront === "function") {
          canvas.bringObjectToFront(circle);
          console.log("ShapesToolbar: Used bringObjectToFront for circle");
        } else {
          console.warn("ShapesToolbar: bringObjectToFront not available");
          const objects = canvas.getObjects();
          const index = objects.indexOf(circle);
          if (index !== -1 && index < objects.length - 1) {
            objects.splice(index, 1);
            objects.push(circle);
            canvas._objects = objects;
            console.log("ShapesToolbar: Manually reordered stack for circle");
          }
        }
      } catch (stackError) {
        console.error(
          "ShapesToolbar: Failed to adjust circle stacking",
          stackError
        );
      }
      canvas.requestRenderAll();
      const bounds = circle.getBoundingRect();
      console.log("ShapesToolbar: Circle added", {
        top: circle.top,
        left: circle.left,
        radius: circle.radius,
        canvasWidth: canvas.getWidth(),
        canvasHeight: canvas.getHeight(),
        stackIndex: canvas.getObjects().indexOf(circle),
        objectCount: canvas.getObjects().length,
        objects: canvas.getObjects().map((obj) => ({
          type: obj.type,
          top: obj.top,
          left: obj.left,
        })),
        bounds: {
          left: bounds.left,
          top: bounds.top,
          width: bounds.width,
          height: bounds.height,
        },
        isVisible:
          bounds.left >= 0 &&
          bounds.top >= 0 &&
          bounds.left + bounds.width <= canvas.getWidth() &&
          bounds.top + bounds.height <= canvas.getHeight(),
      });
    } catch (error) {
      console.error("ShapesToolbar: Failed to add circle", error);
      setSnackbar({
        open: true,
        message: "Error adding circle: " + error.message,
        severity: "error",
      });
    }
  };

  const addTriangle = () => {
    if (!canvas || !canvasScale) {
      setSnackbar({
        open: true,
        message: "Canvas or scale not initialized.",
        severity: "error",
      });
      return;
    }

    console.log("ShapesToolbar: Checking fabric.Triangle", {
      Triangle: Triangle,
      fabricVersion: Triangle.version || "undefined",
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      viewportTransform: canvas.viewportTransform,
      zoom: canvas.getZoom(),
    });
    if (!Triangle) {
      console.error("ShapesToolbar: Triangle is not defined");
      setSnackbar({
        open: true,
        message: "Failed to add triangle. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    try {
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvas.setZoom(1);
      const triangle = new Triangle({
        top: canvas.getHeight() / 2,
        left: canvas.getWidth() / 2,
        width: 100,
        height: 70,
        fill: "orange",
        strokeUniform: true,
        originX: "center",
        originY: "center",
        opacity: 1,
      });
      canvas.add(triangle);
      canvas.setActiveObject(triangle);
      setSelectedObject(triangle);
      try {
        if (typeof canvas.bringObjectToFront === "function") {
          canvas.bringObjectToFront(triangle);
          console.log("ShapesToolbar: Used bringObjectToFront for triangle");
        } else {
          console.warn("ShapesToolbar: bringObjectToFront not available");
          const objects = canvas.getObjects();
          const index = objects.indexOf(triangle);
          if (index !== -1 && index < objects.length - 1) {
            objects.splice(index, 1);
            objects.push(triangle);
            canvas._objects = objects;
            console.log("ShapesToolbar: Manually reordered stack for triangle");
          }
        }
      } catch (stackError) {
        console.error(
          "ShapesToolbar: Failed to adjust triangle stacking",
          stackError
        );
      }
      canvas.requestRenderAll();
      const bounds = triangle.getBoundingRect();
      console.log("ShapesToolbar: Triangle added", {
        top: triangle.top,
        left: triangle.left,
        width: triangle.width,
        height: triangle.height,
        canvasWidth: canvas.getWidth(),
        canvasHeight: canvas.getHeight(),
        stackIndex: canvas.getObjects().indexOf(triangle),
        objectCount: canvas.getObjects().length,
        objects: canvas.getObjects().map((obj) => ({
          type: obj.type,
          top: obj.top,
          left: obj.left,
        })),
        bounds: {
          left: bounds.left,
          top: bounds.top,
          width: bounds.width,
          height: bounds.height,
        },
        isVisible:
          bounds.left >= 0 &&
          bounds.top >= 0 &&
          bounds.left + bounds.width <= canvas.getWidth() &&
          bounds.top + bounds.height <= canvas.getHeight(),
      });
    } catch (error) {
      console.error("ShapesToolbar: Failed to add triangle", error);
      setSnackbar({
        open: true,
        message: "Error adding triangle: " + error.message,
        severity: "error",
      });
    }
  };

  const addEllipse = () => {
    if (!canvas || !canvasScale) {
      setSnackbar({
        open: true,
        message: "Canvas or scale not initialized.",
        severity: "error",
      });
      return;
    }

    console.log("ShapesToolbar: Checking fabric.Ellipse", {
      Ellipse: Ellipse,
      fabricVersion: Ellipse.version || "undefined",
      canvasWidth: canvas.getWidth(),
      canvasHeight: canvas.getHeight(),
      viewportTransform: canvas.viewportTransform,
      zoom: canvas.getZoom(),
    });
    if (!Ellipse) {
      console.error("ShapesToolbar: Ellipse is not defined");
      setSnackbar({
        open: true,
        message: "Failed to add ellipse. Please try refreshing.",
        severity: "error",
      });
      return;
    }

    try {
      canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
      canvas.setZoom(1);
      const ellipse = new Ellipse({
        top: canvas.getHeight() / 2,
        left: canvas.getWidth() / 2,
        rx: 50,
        ry: 25,
        fill: "red",
        stroke: "black",
        strokeWidth: 2,
        strokeUniform: true,
        originX: "center",
        originY: "center",
        opacity: 1,
      });
      canvas.add(ellipse);
      canvas.setActiveObject(ellipse);
      setSelectedObject(ellipse);
      try {
        if (typeof canvas.bringObjectToFront === "function") {
          canvas.bringObjectToFront(ellipse);
          console.log("ShapesToolbar: Used bringObjectToFront for ellipse");
        } else {
          console.warn("ShapesToolbar: bringObjectToFront not available");
          const objects = canvas.getObjects();
          const index = objects.indexOf(ellipse);
          if (index !== -1 && index < objects.length - 1) {
            objects.splice(index, 1);
            objects.push(ellipse);
            canvas._objects = objects;
            console.log("ShapesToolbar: Manually reordered stack for ellipse");
          }
        }
      } catch (stackError) {
        console.error(
          "ShapesToolbar: Failed to adjust ellipse stacking",
          stackError
        );
      }
      canvas.requestRenderAll();
      const bounds = ellipse.getBoundingRect();
      console.log("ShapesToolbar: Ellipse added", {
        top: ellipse.top,
        left: ellipse.left,
        rx: ellipse.rx,
        ry: ellipse.ry,
        canvasWidth: canvas.getWidth(),
        canvasHeight: canvas.getHeight(),
        stackIndex: canvas.getObjects().indexOf(ellipse),
        objectCount: canvas.getObjects().length,
        objects: canvas.getObjects().map((obj) => ({
          type: obj.type,
          top: obj.top,
          left: obj.left,
        })),
        bounds: {
          left: bounds.left,
          top: bounds.top,
          width: bounds.width,
          height: bounds.height,
        },
        isVisible:
          bounds.left >= 0 &&
          bounds.top >= 0 &&
          bounds.left + bounds.width <= canvas.getWidth() &&
          bounds.top + bounds.height <= canvas.getHeight(),
      });
    } catch (error) {
      console.error("ShapesToolbar: Failed to add ellipse", error);
      setSnackbar({
        open: true,
        message: "Error adding ellipse: " + error.message,
        severity: "error",
      });
    }
  };

  return (
    <Box>
      <Typography
        variant="h5"
        sx={{
          color: "#0d2445",
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Create Shapes
      </Typography>
      <Box sx={{ marginTop: 2, marginLeft: 1, marginBottom: 2 }}>
        <Stack direction="row" justifyContent="space-evenly">
          <Card
            elevation={4}
            sx={{
              backgroundColor: "white",
              color: "#0d2445",
              height: 80,
              width: 80,
              marginBottom: 1,
              marginRight: 2,
              marginLeft: -2,
              textAlign: "center",
              ":hover": { boxShadow: 10, backgroundColor: "#ffffff" },
            }}
          >
            <CardActionArea
              sx={{ "& .MuiTouchRipple-ripple": { color: "blue" } }}
              onClick={addRectangle}
            >
              <CropSquareSharpIcon sx={{ marginTop: 2, marginBottom: -1 }} />
              <CardHeader
                title="Square"
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "#000",
                    fontSize: 12,
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    width: 60,
                    marginLeft: -1,
                  },
                }}
              />
            </CardActionArea>
          </Card>
          <Card
            elevation={4}
            sx={{
              backgroundColor: "white",
              color: "#0d2445",
              height: 80,
              width: 80,
              marginBottom: 1,
              marginRight: 2,
              textAlign: "center",
              ":hover": { boxShadow: 10, backgroundColor: "#ffffff" },
            }}
          >
            <CardActionArea
              sx={{ "& .MuiTouchRipple-ripple": { color: "blue" } }}
              onClick={addCircle}
            >
              <CircleOutlinedIcon sx={{ marginTop: 2, marginBottom: -1 }} />
              <CardHeader
                title="Circle"
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "#000",
                    fontSize: 12,
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    width: 60,
                    marginLeft: -1,
                  },
                }}
              />
            </CardActionArea>
          </Card>
          <Card
            elevation={4}
            sx={{
              backgroundColor: "white",
              color: "#0d2445",
              height: 80,
              width: 80,
              marginBottom: 1,
              marginRight: 2,
              textAlign: "center",
              ":hover": { boxShadow: 10, backgroundColor: "#ffffff" },
            }}
          >
            <CardActionArea
              sx={{ "& .MuiTouchRipple-ripple": { color: "blue" } }}
              onClick={addTriangle}
            >
              <ChangeHistoryIcon sx={{ marginTop: 2, marginBottom: -1 }} />
              <CardHeader
                title="Triangle"
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "#000",
                    fontSize: 12,
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    width: 60,
                    marginLeft: -1,
                  },
                }}
              />
            </CardActionArea>
          </Card>
          <Card
            elevation={4}
            sx={{
              backgroundColor: "white",
              color: "#0d2445",
              height: 80,
              width: 80,
              marginBottom: 1,
              marginRight: 2,
              textAlign: "center",
              ":hover": { boxShadow: 10, backgroundColor: "#ffffff" },
            }}
          >
            <CardActionArea
              sx={{ "& .MuiTouchRipple-ripple": { color: "blue" } }}
              onClick={addEllipse}
            >
              <CircleOutlinedIcon sx={{ marginTop: 2, marginBottom: -1 }} />
              <CardHeader
                title="Ellipse"
                sx={{
                  "& .MuiCardHeader-title": {
                    color: "#000",
                    fontSize: 12,
                    fontFamily: "Roboto",
                    fontWeight: 500,
                    width: 60,
                    marginLeft: -1,
                  },
                }}
              />
            </CardActionArea>
          </Card>
        </Stack>
      </Box>
      <Typography
        sx={{
          color: "#0d2445",
          fontSize: 16,
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Create Polygon
      </Typography>
      <Box sx={{ marginTop: 2, marginLeft: 1, marginBottom: 2 }}>
        <PolygonToolbar canvas={canvas} />
      </Box>
      <Typography
        sx={{
          color: "#0d2445",
          fontSize: 16,
          fontFamily: "Roboto",
          fontWeight: 700,
          textAlign: "left",
        }}
      >
        Create Line
      </Typography>
      <Box sx={{ marginTop: 2, marginLeft: 1, marginBottom: 2 }}>
        <LineToolbar canvas={canvas} />
      </Box>
    </Box>
  );
};

export default ShapesToolbar;
